/**
 * VRGrabComponent - Handles grabbing/attachment of grabbable actors.
 */

import * as THREE from 'three';

import { PrimitiveComponent } from '../../components/PrimitiveComponent.js';
import { SceneComponent } from '../../components/SceneComponent.js';
import { PhysicsVectorParam } from '../../physics/PhysicsEngine.js';
import { PhysicsMotionType } from '../../physics/PhysicsTypes.js';
import { EngineClass } from '../../systems/ClassRegistry.js';
import { Delegate } from '../../utils/Delegate.js';
import { VRGrabbableComponent } from '../actor-components/VRGrabbableComponent.js';
import { XRButton } from '../VRControllerManager.js';
import { VRPawn } from '../VRPawn.js';

import type { Actor } from '../../actors/Actor.js';
import type { XRAxis, XRSqueezeEvent } from '../VRControllerManager.js';
import type { IVRInputHandler } from '../VRPawn.js';

@EngineClass('VRGrabComponent')
export class VRGrabComponent extends SceneComponent implements IVRInputHandler {



  private pawn: VRPawn | null = null;
  private attachedActors: Map<'left' | 'right', Actor> = new Map();
  private attachmentOptions: Map<'left' | 'right', ReturnType<VRGrabbableComponent['getVRAttachmentOptions']>> = new Map();
  private hiddenControllers: Map<'left' | 'right', boolean> = new Map();
  private attachmentOffsets: Map<'left' | 'right', {
    position: THREE.Vector3,
    quaternion: THREE.Quaternion,
    scale: THREE.Vector3,
    prevPhysicsOptions: ReturnType<PrimitiveComponent['getPhysicsOptions']> | undefined,
    beam?: {
      distance: number,
      initActorQuat: THREE.Quaternion,
      initControllerQuat: THREE.Quaternion,
      localHitOffset: THREE.Vector3,
    },
  }> = new Map();

  public override beginPlay(): void {
    super.beginPlay();
    const actor = this.getActor();
    if (actor instanceof VRPawn) {
      this.pawn = actor;
      // High priority so grab/interact consume before teleport
      this.pawn.addVRInputHandler(this, 10);
    }
  }

  public override endPlay(): void {
    if (this.pawn) {
      this.pawn.removeVRInputHandler(this);
    }
    this.pawn = null;
    super.endPlay();
  }

  public override tickPrePhysics(deltaTime: number): void {
    super.tickPrePhysics(deltaTime);
    const pawn = this.pawn; if (!pawn) return;
    if (this.attachmentOffsets.size === 0) return;

    for (const [hand, actor] of this.attachedActors.entries()) {
      const actorRoot = actor.rootComponent;
      const off = this.attachmentOffsets.get(hand);
      if (!off) continue;

      const controllerGroup: THREE.Group | null = pawn.getControllerManager()?.getControllerGripGroup(hand) ?? null;
      if (!controllerGroup) continue;

      const controllerWorldPos = new THREE.Vector3();
      const controllerWorldQuat = new THREE.Quaternion();
      controllerGroup.getWorldPosition(controllerWorldPos);
      controllerGroup.getWorldQuaternion(controllerWorldQuat);

      if (off.beam) {
        // Beam: keep initial orientation at pickup, then rotate by wrist delta; position so original hit point stays on beam target
        const rayPose = pawn.getTargetRayPose(hand);
        if (!rayPose) {
          continue;
        }
        const targetPoint = rayPose.origin.clone().add(rayPose.direction.clone().normalize().multiplyScalar(off.beam.distance));
        // Compute wrist delta since pickup
        const deltaRot = controllerWorldQuat.clone().multiply(off.beam.initControllerQuat.clone().invert());
        const worldQuat = deltaRot.multiply(off.beam.initActorQuat.clone());
        const hitOffsetWorld = off.beam.localHitOffset.clone().applyQuaternion(worldQuat);
        const worldPos = targetPoint.clone().sub(hitOffsetWorld);
        actorRoot.setWorldPosition(worldPos);
        actorRoot.setWorldQuaternion(worldQuat);

      } else {
        const worldPos = controllerWorldPos.clone().add(off.position.clone().applyQuaternion(controllerWorldQuat));
        // hand mode: align forward to current target-ray each tick, then apply local offset
        const rayPose = pawn.getTargetRayPose(hand);
        const controller = pawn.getControllerByHand(hand);
        let worldQuat: THREE.Quaternion;
        if (controller) {
          const gripPose = pawn.getControllerManager()!.getGripPose(controller.inputSource);
          if (gripPose && rayPose) {
            const targetDirection = rayPose.direction.clone().normalize();
            const gripUp = new THREE.Vector3(0, 1, 0).applyQuaternion(gripPose.quaternion);
            const rotationMatrix = new THREE.Matrix4().lookAt(new THREE.Vector3(0,0,0), targetDirection.clone().negate(), gripUp);
            const desiredWorldQuat = new THREE.Quaternion().setFromRotationMatrix(rotationMatrix);
            worldQuat = desiredWorldQuat.clone().multiply(off.quaternion);
          } else {
            worldQuat = controllerWorldQuat.clone().multiply(off.quaternion);
          }
        } else {
          worldQuat = controllerWorldQuat.clone().multiply(off.quaternion);
        }
        actorRoot.setWorldPosition(worldPos);
        actorRoot.setWorldQuaternion(worldQuat);
      }
      actorRoot.scale.copy(off.scale);
      // physics transform will be pushed automatically by physicsEngine.tick via component.syncToPhysics
    }
  }

  public handleSelectStart(event: XRSqueezeEvent): boolean {
    const pawn = this.pawn; if (!pawn) return false;
    const hand = event.inputSource.handedness as 'left' | 'right' | 'none';
    if (hand === 'none') return false;
    if (this.getAttachedActor(hand)) return false;


    const target = this.findGrabbableAtHand(hand);
    if (target && target.comp.canGrab() && target.comp.getVRAttachmentInputType() === 'select') {
      this.attachActorToHand(target.actor, target.comp, hand);
      return true;
    }
    return false;
  }

  public handleSelectEnd(event: XRSqueezeEvent): boolean {
    const pawn = this.pawn; if (!pawn) return false;
    const hand = event.inputSource.handedness as 'left' | 'right' | 'none';
    if (hand === 'none') return false;

    const attached = this.getAttachedActor(hand);
    const comp = attached?.getComponent(VRGrabbableComponent) ?? null;
    if (comp && comp.getVRAttachmentInputType() === 'select') {
      const vel = pawn.getControllerVelocity(hand) ?? new THREE.Vector3();
      this.detachActorFromHand(hand, vel.clone().multiplyScalar(3));
      return true;
    }

    return false;
  }

  public handleVRButtonDown(inputSource: XRInputSource, button: XRButton, value: number): boolean {
    const pawn = this.pawn; if (!pawn) return false;
    const hand = inputSource.handedness as 'left' | 'right' | 'none';
    if (hand === 'none') return false;

    if (this.forwardToAttached(hand, button, true, value)) return true;

    if (button === XRButton.Squeeze && !this.getAttachedActor(hand)) {
      const target = this.findGrabbableAtHand(hand);
      if (target && target.comp.canGrab() && target.comp.getVRAttachmentInputType() === 'grip') {
        this.attachActorToHand(target.actor, target.comp, hand);
        return true;
      }
    }
    return false;
  }

  public handleVRButtonUp(inputSource: XRInputSource, button: XRButton): boolean {
    const pawn = this.pawn; if (!pawn) return false;
    const hand = inputSource.handedness as 'left' | 'right' | 'none';
    if (hand === 'none') return false;

    if (this.forwardToAttached(hand, button, false, 0)) return true;

    if (button === XRButton.Squeeze) {
      const attached = this.getAttachedActor(hand);
      const comp = attached?.getComponent(VRGrabbableComponent) ?? null;
      if (comp && comp.getVRAttachmentInputType() === 'grip') {
        const vel = pawn.getControllerVelocity(hand) ?? new THREE.Vector3();
        this.detachActorFromHand(hand, vel.clone().multiplyScalar(3));
        return true;
      }
    }
    return false;
  }

  public handleVRAxisChange(inputSource: XRInputSource, axis: XRAxis, value: number): boolean {
    const pawn = this.pawn; if (!pawn) return false;
    const hand = inputSource.handedness as 'left' | 'right' | 'none';
    if (hand === 'none') return false;
    return this.forwardToAttached(hand, axis, Math.abs(value) > 0.1, value);
  }

  //======================== Attachment Core ========================

  public attachActorToHand(targetActor: Actor, targetComp: VRGrabbableComponent, hand: 'left' | 'right'): void {
    const pawn = this.pawn!;
    // If this hand already holds something, release it first
    this.detachActorFromHand(hand);

    // If the target actor is already held by the other hand, transfer without restoring physics
    const otherHand: 'left' | 'right' = hand === 'left' ? 'right' : 'left';
    const heldByOther = this.attachedActors.get(otherHand);
    const isTransfer = heldByOther === targetActor;
    const existingRecord = isTransfer ? this.attachmentOffsets.get(otherHand) : null;
    if (isTransfer) {
      // Unmap other hand without restoring physics
      this.attachedActors.delete(otherHand);
      this.attachmentOptions.delete(otherHand);
      this.attachmentOffsets.delete(otherHand);
      // Restore controller visibility for the other hand if we had hidden it
      if (this.hiddenControllers.get(otherHand) && pawn.getControllerManager()) {
        pawn.getControllerManager()!.setControllerVisible(otherHand, true);
        this.hiddenControllers.delete(otherHand);
      }
    }

    const options = targetComp.getVRAttachmentOptions();

    // Store attachment locally
    this.attachedActors.set(hand, targetActor);
    this.attachmentOptions.set(hand, options);

    const attachmentMode = options.attachmentMode ?? 'hand';
    const actorRoot = targetActor.rootComponent;

    const controllerGroup: THREE.Group | null = pawn.getControllerManager()!.getControllerGripGroup(hand);
    if (!controllerGroup) return;

    // Compute offset without reparenting
    const controllerWorldPos = new THREE.Vector3();
    const controllerWorldQuat = new THREE.Quaternion();
    controllerGroup.getWorldPosition(controllerWorldPos);
    controllerGroup.getWorldQuaternion(controllerWorldQuat);

    const actorWorldPos = new THREE.Vector3();
    const actorWorldQuat = new THREE.Quaternion();
    const actorWorldScale = new THREE.Vector3();
    actorRoot.getWorldPosition(actorWorldPos);
    actorRoot.getWorldQuaternion(actorWorldQuat);
    actorRoot.getWorldScale(actorWorldScale);

    const controllerWorldQuatInv = controllerWorldQuat.clone().invert();
    let offsetPos = actorWorldPos.clone().sub(controllerWorldPos).applyQuaternion(controllerWorldQuatInv);
    let offsetQuat = controllerWorldQuatInv.multiply(actorWorldQuat);
    let localHitOffset: THREE.Vector3 | undefined;
    let desiredDistance: number | undefined;

    if (attachmentMode === 'beam') {
      // compute local hit offset from ray and actual distance along the ray
      const rayPose = pawn.getTargetRayPose(hand);
      localHitOffset = new THREE.Vector3();
      let actualRayDistance = actorWorldPos.clone().sub(controllerWorldPos).length(); // fallback

      if (rayPose) {
        const world = pawn.getWorld();
        const hits = world?.getPhysicsEngine()?.performHitTest({ origin: rayPose.origin, direction: rayPose.direction, maxDistance: 20, stopOnFirstHit: true }) ?? [];
        const hit = hits.find(h => h.hitActor === targetActor);
        if (hit) {
          localHitOffset.copy(hit.hitLocation.clone().sub(actorWorldPos).applyQuaternion(actorWorldQuat.clone().invert()));
          // Calculate distance along the ray from ray origin to hit point
          actualRayDistance = rayPose.origin.distanceTo(hit.hitLocation);
        }
      }

      let useBeamPickupDistance = options.useBeamPickupDistance ?? false;
      if (useBeamPickupDistance && options.beamPickupDistance !== undefined) {
        // Only clamp to beamPickupDistance if actual distance exceeds it
        desiredDistance = actualRayDistance > options.beamPickupDistance ? options.beamPickupDistance : actualRayDistance;
      } else {
        desiredDistance = actualRayDistance;
      }
      offsetPos = new THREE.Vector3(0, 0, -desiredDistance);
      if (options.offsetScale) actorWorldScale.copy(options.offsetScale);
    } else {
      // hand mode: align forward to target-ray, roll from grip-up, then apply optional local offset
      const rayPose = pawn.getTargetRayPose(hand);
      const controller = pawn.getControllerByHand(hand);
      let desiredWorldQuat: THREE.Quaternion | null = null;
      if (controller && rayPose) {
        const gripPose = pawn.getControllerManager()!.getGripPose(controller.inputSource);
        if (gripPose) {
          const targetDirection = rayPose.direction.clone().normalize();
          const gripUp = new THREE.Vector3(0, 1, 0).applyQuaternion(gripPose.quaternion);
          const rotationMatrix = new THREE.Matrix4().lookAt(new THREE.Vector3(0,0,0), targetDirection.clone().negate(), gripUp);
          desiredWorldQuat = new THREE.Quaternion().setFromRotationMatrix(rotationMatrix);
        }
      }
      // offsetQuat is purely the local offset; desiredWorldQuat is applied at tick
      offsetQuat = options.offsetRotation ? new THREE.Quaternion().setFromEuler(options.offsetRotation) : new THREE.Quaternion();
      offsetPos = options.offsetPosition ? options.offsetPosition.clone() : new THREE.Vector3(0, 0, 0);
      if (options.offsetScale) actorWorldScale.copy(options.offsetScale); else actorWorldScale.set(1,1,1);
    }

    // Set kinematic control and disable receive-from-physics while held (only if not transferring)
    if (actorRoot instanceof PrimitiveComponent) {
      const prevPhysics = isTransfer
        ? (existingRecord?.prevPhysicsOptions ?? actorRoot.getPhysicsOptions())
        : actorRoot.getPhysicsOptions();
      if (!isTransfer) {
        actorRoot.overridePhysicsOptions({
          ...(prevPhysics ?? {}),
          motionType: PhysicsMotionType.KinematicPositionBased,
          enabled: true,
        });
        actorRoot.setPhysicsTransformUpdateFlags({
          sendPosition: true,
          sendRotation: true,
          receivePosition: false,
          receiveRotation: false,
        });
      }

      const record = {
        position: offsetPos,
        quaternion: offsetQuat,
        scale: actorWorldScale.clone(),
        prevPhysicsOptions: prevPhysics ? { ...prevPhysics } : undefined,
      } as {
      position: THREE.Vector3,
      quaternion: THREE.Quaternion,
      scale: THREE.Vector3,
      prevPhysicsOptions: ReturnType<PrimitiveComponent['getPhysicsOptions']> | undefined,
      beam?: { distance: number, initActorQuat: THREE.Quaternion, initControllerQuat: THREE.Quaternion, localHitOffset: THREE.Vector3 },
    };
      if (attachmentMode === 'beam') {
        record.beam = {
          distance: desiredDistance ?? offsetPos.length(),
          initActorQuat: actorWorldQuat.clone(),
          initControllerQuat: controllerWorldQuat.clone(),
          localHitOffset: localHitOffset ?? new THREE.Vector3(),
        };
      }
      this.attachmentOffsets.set(hand, record);
    }

    // Notify grabbable via delegate
    targetComp.onGrabStart.invoke(hand);
    if (options.hideController && pawn.getControllerManager()) {
      pawn.getControllerManager()!.setControllerVisible(hand, false);
      this.hiddenControllers.set(hand, true);
    }
    this.onActorAttachedToHand.invoke(hand, targetActor);
  }

  public detachActorFromHand(hand: 'left' | 'right', dropVelocity?: THREE.Vector3): void {
    const pawn = this.pawn!;
    const actor = this.attachedActors.get(hand);
    if (!actor) return;
    const actorRoot = actor.rootComponent;
    const world = pawn.getWorld();

    if (actorRoot instanceof PrimitiveComponent) {
    // If the same actor is still held by the other hand, do NOT restore physics yet
      const stillHeldByOther = Array.from(this.attachedActors.entries()).some(([h, a]) => h !== hand && a === actor);
      const offset = this.attachmentOffsets.get(hand);
      if (!stillHeldByOther && offset) {
        const prev = offset.prevPhysicsOptions ?? {};
        actorRoot.overridePhysicsOptions({ ...prev });
        actorRoot.setPhysicsTransformUpdateFlags({
          sendPosition: true,
          sendRotation: true,
          receivePosition: true,
          receiveRotation: true,
        });
      }

      // Apply drop velocity if provided
      if (dropVelocity && world) {
        world.getPhysicsEngine()?.setVectorParam(actorRoot, PhysicsVectorParam.LinearVelocity, dropVelocity.toArray());
      }
    }
    // Notify grabbable via delegate
    const comp = actor.getComponent(VRGrabbableComponent);
    comp?.onGrabEnd.invoke(hand, dropVelocity ?? this.pawn?.getControllerVelocity(hand) ?? undefined);

    if (this.hiddenControllers.get(hand) && pawn.getControllerManager()) {
      pawn.getControllerManager()!.setControllerVisible(hand, true);
      this.hiddenControllers.delete(hand);
    }
    this.attachedActors.delete(hand);
    this.attachmentOptions.delete(hand);
    this.onActorDetachedFromHand.invoke(hand);
  }

  private forwardToAttached(hand: 'left' | 'right', control: XRButton | XRAxis, pressed: boolean, value: number): boolean {
    const attached = this.getAttachedActor(hand);
    const comp = attached?.getComponent(VRGrabbableComponent) ?? null;
    comp?.onVRInput.invoke(hand, control, pressed, value);
    return false;
  }

  private findGrabbableAtHand(hand: 'left' | 'right'):
    { actor: Actor, comp: VRGrabbableComponent, distance: number } | null {
    const pawn = this.pawn!;
    const world = pawn.getWorld();
    if (!world) return null;

    const rayPose = pawn.getTargetRayPose(hand);
    if (!rayPose) return null;

    const hits = world.getPhysicsEngine()?.performHitTest({
      origin: rayPose.origin,
      direction: rayPose.direction,
      maxDistance: 10,
      stopOnFirstHit: false,
      ignoredActors: [pawn]
    }) ?? [];

    let best: { actor: Actor, comp: VRGrabbableComponent, distance: number } | null = null;
    for (const hit of hits) {
      const actor = hit.hitActor as Actor;
      const comp = actor?.getComponent(VRGrabbableComponent);
      if (!comp) continue;
      const dist = rayPose.origin.distanceTo(hit.hitLocation);
      if (!best || dist < best.distance) {
        best = { actor, comp, distance: dist };
      }
    }
    return best;
  }

  public getAttachedActor(hand: 'left' | 'right'): Actor | null {
    return this.attachedActors.get(hand) ?? null;
  }

  public readonly onActorAttachedToHand: Delegate<[hand: 'left' | 'right', actor: Actor], void> = new Delegate();
  public readonly onActorDetachedFromHand: Delegate<[hand: 'left' | 'right'], void> = new Delegate();
}


